import { apiFetch } from '../../../lib/apiFetch';
import AppConfig from '../../../lib/AppConfig';
import { cleanApiError, handleResponse } from '../../../utils/handleResponse';
import { buildQuery, fileToBase64, getAPIHeaders, QueryParams } from '../../../utils/helpers';
import {
	CreateGroupRequest,
	UpdateGroupRequest,
	GroupTemplateRequest,
	GroupActionRequest,
	AssignClientsRequest,
	TransferClientsRequest,
	AssignRoleRequest,
	UpdateRoleRequest,
} from '../types/Request';
import {
	GroupSummaryResponse,
	GroupTemplate,
	ListGroupsLiteResponse,
	ListGroupsResponse,
} from '../types/Response';
import { GetGroupResponse } from '../types/response/GetGroup';
import { GroupAccountsResponse } from '../types/response/GroupAccounts';

export namespace GroupRepository {
	const baseUrl = `${AppConfig.api_url}/fineract-provider/api/v1/groups`;

	export async function listGroups(
		params: URLSearchParams = new URLSearchParams(),
	): Promise<ListGroupsResponse> {
		const url = `${baseUrl}?${params.toString()}`;
		return apiFetch(url);
	}

	export async function listGroupsLite(params?: QueryParams): Promise<ListGroupsLiteResponse> {
		const query = new URLSearchParams(buildQuery(params)).toString();
		const url = `${AppConfig.api_url}/gmapis/groups${query ? `?${query}` : ''}`;
		return apiFetch(url);
	}

	export async function getGroupSummary(params?: QueryParams): Promise<GroupSummaryResponse> {
		const query = new URLSearchParams(buildQuery(params)).toString();
		const url = `${AppConfig.api_url}/gmapis/groups/summary?${query ? `?${query}` : ''}`;
		return apiFetch(url);
	}

	export async function getGroup(
		groupId: string | number,
		associations?: string,
	): Promise<GetGroupResponse> {
		const url = `${baseUrl}/${groupId}${associations ? `?associations=${associations}` : '?associations=all'}`;
		return apiFetch(url);
	}

	export async function getGroupAccounts(groupId: string | number): Promise<GroupAccountsResponse> {
		const url = `${baseUrl}/${groupId}/accounts`;
		return apiFetch(url);
	}

	export async function getGroupTemplate(params?: GroupTemplateRequest): Promise<GroupTemplate> {
		const query = new URLSearchParams(buildQuery(params)).toString();
		const url = `${baseUrl}/template${query ? `?${query}` : ''}`;
		return apiFetch(url);
	}

	export async function createGroup(data: CreateGroupRequest): Promise<any> {
		try {
			// 1. Construct payload for Fineract group creation
			const date = new Date();
			const fineractPayload = {
				name: data.name,
				officeId: data.officeId,
				active: data.active,
				...(data.dateFormat && { dateFormat: data.dateFormat }),
				...(data.locale && { locale: data.locale }),
				...(data.active && {
					activationDate:
						data.activationDate ||
						`${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'long' })} ${date.getFullYear()}`,
				}),
				...(data.externalId && { externalId: data.externalId }),
				...(data.staffId && { staffId: data.staffId }),
				...(data.clientMembers?.length ? { clientMembers: data.clientMembers } : {}),
			};

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			// 2. POST to Fineract
			const response = await fetch(baseUrl, {
				method: 'POST',
				headers,
				body: JSON.stringify(fineractPayload),
			});

			const fineractGroup = await handleResponse(response);
			const groupId = fineractGroup?.groupId || fineractGroup?.resourceId;

			if (!groupId) {
				throw new Error('Fineract group creation succeeded but no group ID returned.');
			}

			try {
				// 3. Construct payload for /gmapis/group
				const extraPayload = {
					groupId,
					name: data.name,
					county: data.county,
					address: data.address,
					phone: data.phone,
					registrationCertificate: data.registrationCertificate,
					pin: data.pin,
					email: data.email,
					sector: data.sector,
					introducerId: data.introducerId,
					rlshipOfficerId: data.rlshipOfficerId,
					registrationCertificateFile: data.registrationCertificateFile
						? await fileToBase64(data.registrationCertificateFile)
						: null,
					kraPinFile: data.kraPinFile ? await fileToBase64(data.kraPinFile) : null,
				};

				// 4. POST extra info
				await fetch('/gmapis/group', {
					method: 'POST',
					headers: { ...(await getAPIHeaders()), 'Content-Type': 'application/json' },
					body: JSON.stringify(extraPayload),
				});
			} catch (error: any) {
				console.error('failed to submit extra info', error);
			}

			return fineractGroup;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function updateGroup(
		groupId: number | string,
		data: UpdateGroupRequest,
	): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}`;
			const response = await fetch(url, {
				method: 'PUT',
				headers: { ...(await getAPIHeaders()), 'Content-Type': 'application/json' },
				body: JSON.stringify(data),
			});
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function deleteGroup(groupId: number | string): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}`;
			const response = await fetch(url, {
				method: 'DELETE',
				headers: await getAPIHeaders(),
			});
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	async function postCommand(
		groupId: string | number,
		command: string,
		data: any = {},
	): Promise<any> {
		const url = `${baseUrl}/${groupId}?command=${command}`;
		return apiFetch(url, {
			body: JSON.stringify(data),
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
		});
	}

	export async function activateGroup(
		groupId: string | number,
		data: GroupActionRequest,
	): Promise<any> {
		return postCommand(groupId, 'activate', data);
	}

	export async function associateClients(
		groupId: string | number,
		data: AssignClientsRequest,
	): Promise<any> {
		return postCommand(groupId, 'associateClients', data);
	}

	export async function disassociateClients(
		groupId: string | number,
		data: AssignClientsRequest,
	): Promise<any> {
		return postCommand(groupId, 'disassociateClients', data);
	}

	export async function transferClients(
		groupId: string | number,
		data: TransferClientsRequest,
	): Promise<any> {
		return postCommand(groupId, 'transferClients', data);
	}

	export async function assignStaff(
		groupId: string | number,
		data: { staffId: number; inheritStaffForClientAccounts?: boolean },
	): Promise<any> {
		return postCommand(groupId, 'assignStaff', data);
	}

	export async function unassignStaff(
		groupId: string | number,
		data: { staffId: number },
	): Promise<any> {
		return postCommand(groupId, 'unassignStaff', data);
	}

	export async function closeGroup(
		groupId: string | number,
		data: GroupActionRequest,
	): Promise<any> {
		return postCommand(groupId, 'close', data);
	}

	export async function assignRole(
		groupId: string | number,
		data: AssignRoleRequest,
	): Promise<any> {
		return postCommand(groupId, 'assignRole', data);
	}

	export async function unassignRole(groupId: string | number, roleId: number): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}?command=unassignRole&roleId=${roleId}`;
			const response = await fetch(url, {
				method: 'POST',
				headers: { ...(await getAPIHeaders()), 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			});
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function updateRole(
		groupId: string | number,
		roleId: number,
		data: UpdateRoleRequest,
	): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}?command=updateRole&roleId=${roleId}`;
			const response = await fetch(url, {
				method: 'POST',
				headers: { ...(await getAPIHeaders()), 'Content-Type': 'application/json' },
				body: JSON.stringify(data),
			});
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function generateCollectionSheet(groupId: string | number, data: any): Promise<any> {
		return postCommand(groupId, 'generateCollectionSheet', data);
	}

	export async function saveCollectionSheet(groupId: string | number, data: any): Promise<any> {
		return postCommand(groupId, 'saveCollectionSheet', data);
	}

	export async function getGlimAccounts(groupId: string | number): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}/glimaccounts`;
			const response = await fetch(url, { headers: await getAPIHeaders() });
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}
	export async function getGsimAccounts(groupId: string | number): Promise<any> {
		try {
			const url = `${baseUrl}/${groupId}/gsimaccounts`;
			const response = await fetch(url, { headers: await getAPIHeaders() });
			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}
}
